import React, { useState, useEffect } from 'react';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Button,
    TextField,
    Modal,
    Typography,
    IconButton,
    Chip,
    Tooltip,
    Paper,
    Stack,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Select,
    MenuItem,
    FormControl,
    InputLabel
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    Visibility as ViewIcon,
    LocalShipping as VehicleIcon,
    ToggleOn as ToggleOnIcon,
    ToggleOff as ToggleOffIcon
} from '@mui/icons-material';
import { useApiHandler } from '../../../hooks/useApiHandler';
import { vehicleService } from '../../../services/vehicleService';
import VehicleForm from './VehicleForm';

const VehicleList = ({ onDataChange }) => {
    const { showSuccess, showError } = useApiHandler();
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingVehicle, setEditingVehicle] = useState(null);
    const [viewMode, setViewMode] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [pagination, setPagination] = useState(null);
    const [deleteDialog, setDeleteDialog] = useState({ open: false, vehicle: null });

    // Typing in the search box fetches once the user pauses, not per keystroke
    const debouncedSearchText = useDebouncedValue(searchText);

    useEffect(() => {
        fetchVehicles(debouncedSearchText);
    }, [page, rowsPerPage, debouncedSearchText, statusFilter]);

    const fetchVehicles = async (search = searchText) => {
        setLoading(true);
        try {
            const params = {
                page: page + 1, // Backend expects 1-based page numbers
                per_page: rowsPerPage
            };
            if (search) params.search = search;
            if (statusFilter !== '') params.status = statusFilter;

            const response = await vehicleService.getAllVehicles(params);
            if (response.success) {
                setVehicles(response.data || []);
                setTotalCount(response.pagination?.total || 0);
                setPagination(response.pagination);
                if (onDataChange) {
                    onDataChange();
                }
            } else {
                showError(response.message || 'Failed to fetch vehicles');
            }
        } catch (error) {
            console.error('Error fetching vehicles:', error);
            showError(error.message || 'Failed to fetch vehicles. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (value) => {
        setSearchText(value);
        setPage(0);
    };

    const handleStatusFilter = (status) => {
        setStatusFilter(status);
        setPage(0);
    };

    const handleAdd = () => {
        setEditingVehicle(null);
        setViewMode(false);
        setIsModalVisible(true);
    };

    const handleEdit = (vehicle) => {
        setEditingVehicle(vehicle);
        setViewMode(false);
        setIsModalVisible(true);
    };

    const handleView = (vehicle) => {
        setEditingVehicle(vehicle);
        setViewMode(true);
        setIsModalVisible(true);
    };

    const handleDeleteClick = (vehicle) => {
        setDeleteDialog({ open: true, vehicle });
    };

    const handleDeleteConfirm = async () => {
        if (deleteDialog.vehicle) {
            try {
                const response = await vehicleService.deleteVehicle(deleteDialog.vehicle.id);
                if (response.success) {
                    showSuccess('Vehicle deleted successfully');
                    fetchVehicles();
                } else {
                    showError(response.message || 'Failed to delete vehicle');
                }
            } catch (error) {
                console.error('Error deleting vehicle:', error);
                showError(error.message || 'Failed to delete vehicle. Please try again.');
            }
        }
        setDeleteDialog({ open: false, vehicle: null });
    };

    const handleToggleStatus = async (vehicle) => {
        try {
            const response = await vehicleService.toggleVehicleStatus(vehicle.id);
            if (response.success) {
                showSuccess('Vehicle status updated successfully');
                fetchVehicles();
            } else {
                showError(response.message || 'Failed to update vehicle status');
            }
        } catch (error) {
            console.error('Error updating vehicle status:', error);
            showError(error.message || 'Failed to update vehicle status');
        }
    };

    const handleModalClose = () => {
        setIsModalVisible(false);
        setEditingVehicle(null);
        setViewMode(false);
    };

    const handleFormSuccess = () => {
        setIsModalVisible(false);
        setEditingVehicle(null);
        setViewMode(false);
        fetchVehicles();
        const action = editingVehicle ? 'updated' : 'created';
        showSuccess(`Vehicle ${action} successfully`);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const getStatusChip = (status) => {
        return status ? (
            <Chip label="Active" color="success" size="small" />
        ) : (
            <Chip label="Inactive" color="default" size="small" />
        );
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h6" component="h2">
                        Vehicle List
                    </Typography>
                    {pagination && (
                        <Typography variant="caption" color="text.secondary">
                            Showing {pagination.from || 0} to {pagination.to || 0} of {pagination.total || 0} vehicles
                        </Typography>
                    )}
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAdd}
                >
                    Add Vehicle
                </Button>
            </Box>

            {/* Filters */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <TextField
                    placeholder="Search by number or type..."
                    value={searchText}
                    onChange={(e) => handleSearch(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ minWidth: 300 }}
                />

                <FormControl sx={{ minWidth: 180 }}>
                    <InputLabel id="status-filter-label">Status Filter</InputLabel>
                    <Select
                        labelId="status-filter-label"
                        value={statusFilter}
                        label="Status Filter"
                        onChange={(e) => handleStatusFilter(e.target.value)}
                    >
                        <MenuItem value="">All Status</MenuItem>
                        <MenuItem value="1">Active</MenuItem>
                        <MenuItem value="0">Inactive</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            {/* Table */}
            <TableContainer component={Paper} sx={{ position: 'relative' }}>
                {loading && (
                    <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        bgcolor: 'rgba(255,255,255,0.8)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1
                    }}>
                        <CircularProgress />
                    </Box>
                )}
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>#</TableCell>
                            <TableCell>Vehicle Number</TableCell>
                            <TableCell>Vehicle Type</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Created Date</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {vehicles.length === 0 && !loading ? (
                            <TableRow key="no-data">
                                <TableCell colSpan={6} align="center">
                                    <Typography variant="body2" color="text.secondary">
                                        No vehicles found
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            vehicles.map((vehicle, index) => (
                                <TableRow key={vehicle.id}>
                                    <TableCell>{(pagination?.from || (page * rowsPerPage + 1)) + index}</TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <VehicleIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                            {vehicle.vehicle_number}
                                        </Box>
                                    </TableCell>
                                    <TableCell>{vehicle.vehicle_type}</TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {getStatusChip(vehicle.status)}
                                            <Tooltip title={vehicle.status ? 'Click to deactivate' : 'Click to activate'}>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleToggleStatus(vehicle)}
                                                        color={vehicle.status ? 'success' : 'default'}
                                                    >
                                                        {vehicle.status ? <ToggleOnIcon /> : <ToggleOffIcon />}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        {vehicle.created_at ? new Date(vehicle.created_at).toLocaleDateString() : 'N/A'}
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1}>
                                            <Tooltip title="View Details">
                                                <IconButton size="small" onClick={() => handleView(vehicle)}>
                                                    <ViewIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Edit Vehicle">
                                                <IconButton size="small" onClick={() => handleEdit(vehicle)}>
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete Vehicle">
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleDeleteClick(vehicle)}
                                                        color="error"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />

            {/* Vehicle Form Modal */}
            <Modal
                open={isModalVisible}
                onClose={handleModalClose}
                aria-labelledby="vehicle-modal-title"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '90%', sm: '600px' },
                    maxHeight: '90vh',
                    overflow: 'auto',
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    boxShadow: 24,
                    p: 0
                }}>
                    <VehicleForm
                        vehicle={editingVehicle}
                        onSuccess={handleFormSuccess}
                        onCancel={handleModalClose}
                        viewMode={viewMode}
                    />
                </Box>
            </Modal>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialog.open}
                onClose={() => setDeleteDialog({ open: false, vehicle: null })}
                aria-labelledby="delete-dialog-title"
            >
                <DialogTitle id="delete-dialog-title">
                    Confirm Delete
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete the vehicle "{deleteDialog.vehicle?.vehicle_number}"?
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setDeleteDialog({ open: false, vehicle: null })}
                        color="inherit"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        color="error"
                        variant="contained"
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default VehicleList;
